import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Box, AvatarGroup, Avatar, IconButton } from '@mui/material';
import { NavigateBefore } from '@mui/icons-material';
import { ParticipantView } from '@app/views';
import { useDirectMessageController } from './DirectMessageController';
import { ChatConversation } from './ChatConversation';

export const DirectMessageScreen: React.FC = () => {
  const navigate = useNavigate();
  const { channel, groupName, headerHeight, dmRecipient, participantRole, notifyParticipant } =
    useDirectMessageController();

  return (
    <div id="direct-message-chat" className="stream-chat">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: `calc(100dvh - ${headerHeight}px)`,
          // Fallback for unsupported browsers:
          '@supports not (height: 100dvh)': {
            height: `calc(100vh - ${headerHeight}px)`,
          },
          overflow: 'hidden',
          // Respect iOS home indicator
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {/* Chat header */}
        <Box
          className="chat-header"
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            borderBottom: '1px solid rgba(0,0,0,0.12)',
            backgroundColor: 'background.paper',
          }}
        >
          <IconButton
            edge="start"
            onClick={() => navigate(-1)}
            sx={{
              padding: 0,
              margin: 0,
              minWidth: 'auto',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6d6d6d',
            }}
          >
            <NavigateBefore />
          </IconButton>

          <AvatarGroup
            max={4}
            spacing={15}
            sx={{
              flexDirection: 'row-reverse',
              justifyContent: 'start',

              // avatar sizing & border
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                border: '2px solid #fff',
                backgroundColor: '#fff', // Ensures avatars with transparency have a default background
              },

              '& .MuiAvatar-colorDefault': {
                zIndex: 1000, // ensures +5 etc. avatars are on top
                backgroundColor: '#42797C',
              },
            }}
          >
            {dmRecipient?.map((participant: ParticipantView, index) => (
              <Avatar
                key={participant.id}
                alt={participant.name || participant.email}
                src={participant.avatarUrl || undefined}
                sx={{ zIndex: index + 1 }}
                slotProps={{ img: { referrerPolicy: 'no-referrer' } }}
              />
            ))}
          </AvatarGroup>

          <Box sx={{ flexGrow: 1, ml: 2, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 'bold', lineHeight: 1.2, mb: 0 }}>
              {groupName}
            </Typography>
            <Typography
              variant="caption"
              noWrap
              sx={{
                color: 'text.secondary',
                lineHeight: 1.2,
                mt: 0,
              }}
            >
              {/* Participant Title */}
              {participantRole ? participantRole : 'Hater'}
            </Typography>
          </Box>
        </Box>

        {/* Stream Chat */}
        {channel && <ChatConversation channel={channel} notify={notifyParticipant} />}
      </Box>
    </div>
  );
};
